import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Handshake, Wallet, TrendingUp, PieChart, FileBarChart2, ArrowUpRight, Percent, Users, Building2, LineChart as LineIcon } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const monthOptions = (() => {
  const opts = [];
  const now = new Date();
  for (let i = -5; i <= 1; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const kode = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ kode, label: d.toLocaleDateString("id-ID", { month: "long", year: "numeric" }) });
  }
  return opts;
})();

const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

export const InvestorDashboardPage = () => {
  const currentKode = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const [periode, setPeriode] = useState(currentKode);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/investor/dashboard?periode=${periode}`, { withCredentials: true });
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [periode]);

  const periodeLabel = monthOptions.find((o) => o.kode === periode)?.label || periode;
  const m = data?.matched_mitra;

  return (
    <div className="space-y-6" data-testid="investor-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-[#0e1628] to-[#122036] border border-slate-800 shadow-xl">
        <div>
          <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 text-[10px] font-bold uppercase tracking-wider">Investor Portal</span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Halo, {data?.user_name || "Investor"} 👋</h1>
          <p className="text-xs text-slate-400 mt-0.5">Ringkasan bagi hasil & laporan kinerja periode {periodeLabel}.</p>
        </div>
        <select value={periode} onChange={(e) => setPeriode(e.target.value)} data-testid="investor-periode" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold">
          {monthOptions.map((o) => <option key={o.kode} value={o.kode}>{o.label}</option>)}
        </select>
      </div>

      {loading && <div className="p-10 text-center text-slate-500 text-sm">Memuat dashboard…</div>}

      {!loading && data && (
        <>
          {/* Bagi Hasil Anda */}
          {m ? (
            <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-900/40 to-indigo-900/20 border border-purple-500/40 shadow-xl" data-testid="investor-share-card">
              <div className="flex items-center gap-2 mb-4">
                <Handshake className="w-5 h-5 text-purple-300" />
                <h2 className="text-sm font-bold text-white">Bagi Hasil Anda — {m.nama}</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><p className="text-[11px] text-slate-400 flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Modal Ditanam</p><p className="text-lg font-black text-white mt-1 font-mono">{rp(m.jumlah_modal)}</p></div>
                <div><p className="text-[11px] text-slate-400 flex items-center gap-1"><Percent className="w-3.5 h-3.5" /> Porsi Sharing</p><p className="text-lg font-black text-purple-300 mt-1">{m.prosentase_sharing}%</p></div>
                <div><p className="text-[11px] text-slate-400 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Pelanggan Terkait</p><p className="text-lg font-black text-white mt-1">{m.jumlah_pelanggan}</p></div>
                <div className="p-3 -m-1 rounded-xl bg-emerald-500/10 border border-emerald-500/30"><p className="text-[11px] text-slate-300 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Bagi Hasil Bulan Ini</p><p className="text-xl font-black text-emerald-400 mt-1 font-mono" data-testid="investor-bagian">{rp(m.bagian)}</p></div>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-2xl bg-amber-950/20 border border-amber-500/30 text-amber-300 text-xs" data-testid="investor-no-match">
              Akun Anda belum tertaut ke data mitra manapun. Minta admin membuka <span className="font-semibold">Pengaturan → Pengguna</span>, edit akun Anda, lalu pilih data Mitra/Investor pada kolom tautan. Data bagi hasil akan tampil setelah akun tertaut.
            </div>
          )}

          {/* Ringkasan Keuangan (scope pelanggan sharing Anda) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-xs text-slate-400 font-semibold">{data.scope_locked ? "Laba Bersih (Pelanggan Anda)" : "Laba Bersih Perusahaan"}</p><p className="text-2xl font-black text-cyan-400 mt-1 font-mono">{rp(data.laba_bersih)}</p></div>
            <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-xs text-slate-400 font-semibold">{data.scope_locked ? "Bagi Hasil Anda" : "Total Bagian Semua Mitra"}</p><p className="text-2xl font-black text-purple-400 mt-1 font-mono">{rp(data.mitra_total)}</p></div>
            <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800"><p className="text-xs text-slate-400 font-semibold">{data.scope_locked ? "Bagian Perusahaan (dari pelanggan Anda)" : "Bagian Perusahaan"}</p><p className="text-2xl font-black text-emerald-400 mt-1 font-mono">{rp(data.bagian_perusahaan)}</p></div>
          </div>

          {/* Riwayat Bagi Hasil */}
          <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl" data-testid="investor-history-chart">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2.5 mb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2"><LineIcon className="w-4 h-4 text-emerald-400" /> Riwayat Bagi Hasil (6 Bulan Terakhir)</h3>
              <span className="text-[11px] text-slate-400">Porsi {m ? `${m.prosentase_sharing}%` : "-"}</span>
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={(data.history || []).map((h) => ({ ...h, label: new Date(h.periode + "-01").toLocaleDateString("id-ID", { month: "short", year: "2-digit" }) }))}>
                  <defs>
                    <linearGradient id="bagiGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
                  <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(val) => rp(val)} contentStyle={{ backgroundColor: "#0f172a", borderColor: "#334155", borderRadius: "0.5rem", color: "#f1f5f9", fontSize: "12px" }} />
                  <Area type="monotone" dataKey="bagian" name="Bagi Hasil" stroke="#10b981" strokeWidth={2} fill="url(#bagiGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Mini Laba Rugi */}
            <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
              <h3 className="text-sm font-bold text-white border-b border-slate-800 pb-2.5 mb-2 flex items-center gap-2"><FileBarChart2 className="w-4 h-4 text-purple-400" /> Ringkasan Laba Rugi</h3>
              <table className="w-full text-left text-xs">
                <tbody>
                  {data.laba_rugi_rows.map((r, idx) => {
                    const isTotal = r.tipe === "total";
                    const isSub = r.tipe === "subtotal";
                    const isFinal = r.tipe === "final";
                    const isAlokasi = r.tipe === "alokasi";
                    const color = r.tipe === "minus" ? "text-rose-400"
                      : r.tipe === "plus" ? "text-emerald-400"
                      : isAlokasi ? "text-amber-400"
                      : isFinal ? "text-emerald-300"
                      : isTotal ? (data.laba_bersih >= 0 ? "text-cyan-400" : "text-rose-400")
                      : "text-white";
                    return (
                      <tr key={idx} className={`${isTotal || isFinal ? "border-t-2 border-slate-700" : isSub ? "border-t border-slate-800" : "border-b border-slate-800/50"}`}>
                        <td className={`py-2 px-2 ${isTotal || isSub || isFinal ? "font-black text-white" : "text-slate-300"}`}>{(r.tipe === "minus" || isAlokasi) && "( − ) "}{r.label}</td>
                        <td className={`py-2 px-2 text-right font-mono font-bold ${color} ${isTotal || isFinal ? "text-base" : ""}`}>{rp(r.value)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Distribusi semua mitra */}
            <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-800 pb-2.5 mb-2">
                <h3 className="text-sm font-bold text-white flex items-center gap-2"><PieChart className="w-4 h-4 text-purple-400" /> Distribusi Bagi Hasil</h3>
                <Link to="/laporan" className="text-[11px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1" data-testid="investor-goto-laporan">Laporan lengkap <ArrowUpRight className="w-3.5 h-3.5" /></Link>
              </div>
              <div className="space-y-2">
                {data.mitra_sharing.length === 0 && <p className="text-xs text-slate-500 py-4 text-center">Belum ada data mitra.</p>}
                {data.mitra_sharing.map((x) => (
                  <div key={x.id} className={`p-3 rounded-xl flex items-center justify-between text-xs ${m && x.id === m.id ? "bg-purple-500/15 border border-purple-500/40" : "bg-slate-900"}`}>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-slate-200 font-semibold">{x.nama}</span>
                      <span className="text-[10px] text-purple-300">{x.prosentase_sharing}%</span>
                    </div>
                    <span className="font-mono font-bold text-emerald-400">{rp(x.bagian)}</span>
                  </div>
                ))}
                {!data.scope_locked && (
                  <div className="p-3 rounded-xl flex items-center justify-between text-xs bg-slate-950 border border-slate-800">
                    <span className="text-slate-300 font-bold">Bagian Perusahaan</span>
                    <span className="font-mono font-bold text-white">{rp(data.bagian_perusahaan)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
